/**
 * CAOS-1674 — how an address reads, and nothing else.
 *
 * This module imports nothing and defines no element, for the reason `place-format.ts` gives: a
 * caller that only needs the words — the host's list-view data source, which is pure of the DOM —
 * can have them without registering every component in the library as a side effect of importing
 * the `@caos/ui` barrel.
 *
 * The kernel's display block says the same thing in the same words
 * (`formatAddressDisplay` in `kernel/src/api/record-serialization.ts`), which is what a list view
 * shows, so a record page and a list never show one address two ways.
 *
 * The two are held together by a table of cases that each side's tests assert against:
 * `kernel/test/fixtures/address-display-cases.ts` and the mirror of it in
 * `caos-ui/test/address-format.test.ts`. It is a mirror rather than one shared import because the
 * two packages are separate TypeScript programs and caos-ui's test program does not reference the
 * kernel's test tree. Each file names the other.
 */

/**
 * The parts, in the order an address is written, and the word a person reads for each.
 *
 * DELIBERATELY A SECOND COPY of the kernel's `ADDRESS_PARTS` and `ADDRESS_PART_LABELS`, and the
 * duplication is the sanctioned shape here rather than an oversight. `field-renderer.ts` imports
 * from `@caos/kernel/meta` TYPE-ONLY on purpose — "no kernel/zod runtime is pulled into this leaf
 * library" — so a value import would drag the kernel into every page that draws a field.
 *
 * The same trade was already made for a place: `formatPlace` here and `formatGeolocationDisplay` in
 * the kernel are two implementations of one rendering, neither importing the other, and each pinned
 * by the same cases in its own suite — `PLACE_READS` in `caos-ui/test/location-field.test.ts` and
 * the CAOS-620 display block in `kernel/test/unit/api-geolocation-wire.test.ts`. The address pair
 * follows that precedent with the cases lifted into one named file:
 * `kernel/test/fixtures/address-display-cases.ts`, which the kernel's test imports and
 * `caos-ui/test/address-format.test.ts` keeps a mirror of, each naming the other. A mirror rather
 * than an import because the two packages are separate TypeScript programs and a cross-package
 * import of a test-tree file would not typecheck — so like the place pair, the boundary is still
 * crossed by hand. The part list is pinned more strongly still — a test
 * asserts it equals the kernel's `ADDRESS_PARTS`, which it CAN import because that one lives in
 * shipped source (`@caos/kernel/meta`) rather than in a test tree, so a part added on one side
 * cannot land without the other.
 *
 * THE LABELS ARE NOT PINNED TO THE KERNEL'S, because they are not the same strings doing the same
 * job. The kernel's `ADDRESS_PART_LABELS` are sentence fragments for a validator message — "must
 * include a postal code" — so they are lower case and spell `state` out as "state or province".
 * These are input labels on a form, so they are capitalised and read "State/Province", matching the
 * standard Account field's own label. Asserting the two equal would force one of them to be wrong
 * in its own context; what must agree is the KEYS, and that is what the test checks.
 */
export const ADDRESS_PARTS = ['street', 'city', 'state', 'postal_code', 'country'] as const;
export type AddressPart = (typeof ADDRESS_PARTS)[number];

/** The word a PERSON reads for each part — never the stored key, which nobody writes in a sentence. */
export const ADDRESS_PART_LABELS: Readonly<Record<AddressPart, string>> = {
  street: 'Street',
  city: 'City',
  state: 'State/Province',
  postal_code: 'Postal code',
  country: 'Country',
};

/** An address as its named parts. Every one optional: an address with some parts is still an address. */
export interface AddressValue {
  readonly street?: string | null;
  readonly city?: string | null;
  readonly state?: string | null;
  readonly postal_code?: string | null;
  readonly country?: string | null;
}

/** Present and not blank. A part that is whitespace is a part somebody left empty. */
function filled(part: string | null | undefined): string | null {
  if (typeof part !== 'string') return null;
  const trimmed = part.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * An address as its lines, in the order an address is written, with nothing empty in the list.
 *
 * The state and the postal code share a line and are joined by a SPACE rather than a comma, because
 * that is how a postal line is written everywhere that has both — "CA 94105", never "CA, 94105".
 *
 * EVERY JOIN HERE IS OVER AN ALREADY-FILTERED LIST, which is the whole difficulty of formatting a
 * value whose parts are all optional. Interpolating the parts into a template and hoping would
 * leave a stray comma for every absent part, a leading one for an address with no street, and a
 * bare " " for a postal line with neither half — and all three read as a rendering bug rather than
 * as missing data.
 */
export function formatAddressLines(address: AddressValue): readonly string[] {
  const postalLine = [filled(address.state), filled(address.postal_code)].filter((p): p is string => p !== null).join(' ');
  return [filled(address.street), filled(address.city), postalLine === '' ? null : postalLine, filled(address.country)].filter(
    (line): line is string => line !== null,
  );
}

/**
 * An address on one line: `1 Market St, San Francisco, CA 94105, United States`.
 *
 * The lines above, joined with commas. An address with nothing filled in returns the empty string
 * rather than a comma or a space — and the save path refuses that value anyway, because no address
 * is null and not an empty object.
 */
export function formatAddress(address: AddressValue): string {
  return formatAddressLines(address).join(', ');
}
